import { createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { SERVICES, SERVICE_GALLERIES } from "#/lib/constants";
import { img } from "#/lib/images";
import { ServiceDetailSection } from "#/components/pages/services";
import { FinalCTA } from "#/components/pages/home";
import { PageHeader } from "#/components/shared/PageHeader";

export const Route = createFileRoute("/services_/$slug")({
  loader: ({ params }) => {
    const index = SERVICES.findIndex((s) => s.slug === params.slug);
    if (index === -1) throw notFound();
    return { index };
  },
  head: ({ params }) => {
    const service = SERVICES.find((s) => s.slug === params.slug);
    const title = service
      ? `${service.title} in Delaware | Blue Rock Concrete`
      : "Our Work | Blue Rock Concrete";
    const description = service?.blurb ?? "";
    const url = `https://bluerockconcreteco.com/services/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ServicePage,
});

function ServicePage() {
  const { index } = Route.useLoaderData();
  const service = SERVICES[index];
  const gallery = SERVICE_GALLERIES[service.slug];
  // The header already shows service.img (always gallery[0]); never repeat a
  // photo within one page.
  const galleryPhotos = gallery.filter((photo) => photo !== service.img);

  return (
    <main>
      <PageHeader
        eyebrow="Our Services"
        title={service.title}
        intro={service.blurb}
        image={img(service.img, { w: 1920, h: 1080, q: 70 })}
        imageAlt={`${service.title} by Blue Rock Concrete`}
        breadcrumb={
          <a
            href="/services"
            className="inline-flex items-center gap-2 text-sm font-semibold text-flame no-underline transition-colors hover:text-flame-light"
          >
            <ArrowLeft className="h-4 w-4" />
            All services
          </a>
        }
      />

      {/* Finishes gallery */}
      {galleryPhotos.length > 0 && (
        <section className="bg-graphite pt-14 pb-14 lg:pt-20 lg:pb-20">
          <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3 lg:px-8">
            {galleryPhotos.map((photo, i) => (
              <img
                key={photo}
                src={img(photo, { w: i === 0 ? 1200 : 640, h: i === 0 ? 800 : 480 })}
                alt={`${service.title} finished project, photo ${i + 1} of ${galleryPhotos.length}`}
                width={i === 0 ? 1200 : 640}
                height={i === 0 ? 800 : 480}
                loading={i < 2 ? "eager" : "lazy"}
                decoding="async"
                className={`w-full rounded-lg object-cover ring-1 ring-white/10 ${
                  i === 0 ? "aspect-3/2 sm:col-span-2 sm:row-span-2 sm:h-full" : "aspect-4/3"
                }`}
              />
            ))}
          </div>
        </section>
      )}

      {/* What it looks like + how we build it (reused standard block) */}
      <ServiceDetailSection service={service} index={index} />

      <FinalCTA />
    </main>
  );
}
